import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { produce } from 'immer'
import { find, findIndex } from 'lodash-es'
import { TypeOf } from 'yup'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Button, Stack } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { HotkeyDeleteButton, HotkeySaveButton, SaveButton } from '@ui/actions'
import { Form } from '@ui/forms'
import EditableLabel from '@ui/forms/EditableLabel'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore, useSelectionStore } from '@editorStores'
import { useResultsInvalidation } from '@editorHooks'
import { CollapsibleGeometryBoxSection } from '@editorComponents'
import { useStructuralPlanningFormEsc } from '@structuralPlanningHooks'
import { getModel, getProjectFiles } from '@queries'
import { deleteRoof, updateRoofSlab, addRoof } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import { documentationRootGuid } from 'src/constants/files'
import { schema } from '../../schema'
import FormFields from '../FormFields'
import RoofFormScene from '../RoofFormScene'
import RoofSlabAlignmentForm from '../RoofSlabAlignmentForm'

interface Props {
  selectedElement: string
  setSelectedElement: (element: string | null) => void
  onClose: () => void
}

const RoofFormBase = ({ selectedElement, setSelectedElement, onClose }: Props): ReactElement => {
  const { projectId } = useParams()

  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const invalidateResults = useResultsInvalidation()

  const {
    model: { roof_slabs: roofSlabs },
  } = useModelStore(state => state)

  const roof = useMemo(
    () => (selectedElement ? find(roofSlabs, { guid: selectedElement }) : undefined),
    [roofSlabs, selectedElement],
  ) as ShapeObject

  const defaultValues = useMemo(() => {
    return {
      ...schema.getDefault(),
      guid: roof?.guid,
      points: roof?.shape.points.map(p => ({ x: p.x, y: p.y, z: p.z })),
    }
  }, [roof])

  useStructuralPlanningFormEsc(() => {
    onClose()
  }, !!selectedElement)

  // MUTATIONS

  const { mutateAsync, isLoading } = useMutation(
    (data: TypeOf<typeof schema>) =>
      updateRoofSlab.request(projectId as string, data.guid, data.points),
    {
      onSuccess: async (data: ShapeObject) => {
        const queryData = queryClient.getQueryData<PlanarModel>(getModel.getKey(projectId))
        if (queryData) {
          const newModel = produce(queryData, draft => {
            const roofIndex = findIndex(draft.roof_slabs, { guid: data.guid })
            if (roofIndex !== -1) {
              draft.roof_slabs[roofIndex] = {
                ...draft.roof_slabs[roofIndex],
                ...data,
              }
            }

            return draft
          })
          queryClient.setQueryData(getModel.getKey(projectId), newModel)
        }
        invalidateResults(projectId as string)

        enqueueSnackbar('Dachfläche erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Dachfläche'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: deleteAsync, isLoading: isDeleting } = useMutation(
    (guid: string) => deleteRoof.request(projectId as string, guid),
    {
      onSuccess: async () => {
        setSelectedElement(null)
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        await queryClient.invalidateQueries(
          getProjectFiles.getKey(projectId, documentationRootGuid),
        )
        invalidateResults(projectId as string)
        enqueueSnackbar('Dachfläche erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen der Dachfläche'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: duplicateAsync, isLoading: isDuplicating } = useMutation(
    async () => {
      if (!roof || !projectId) throw new Error('Roof or projectId is missing')

      // Step 1: Create a new roof slab
      const newModel = await addRoof.request(projectId)

      // Find the newly created roof slab (it should be the last one or have a new GUID)
      // We'll find it by comparing with existing roof slabs
      const existingGuids = new Set(roofSlabs.map(r => r.guid))
      const newRoofSlab = newModel.roof_slabs.find((r: ShapeObject) => !existingGuids.has(r.guid))

      if (!newRoofSlab) {
        throw new Error('Failed to find newly created roof slab')
      }

      // Step 2: Translate points by 1 in z direction
      const translatedPoints = roof.shape.points.map(p => ({
        x: p.x,
        y: p.y,
        z: p.z + 1,
      }))

      // Step 3: Patch the new roof slab with translated points
      await updateRoofSlab.request(projectId, newRoofSlab.guid, translatedPoints)

      return newRoofSlab.guid
    },
    {
      onSuccess: async newRoofGuid => {
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        invalidateResults(projectId as string)
        setSelectedElement(newRoofGuid)
        enqueueSnackbar('Dachfläche erfolgreich dupliziert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Duplizieren der Dachfläche'), {
          variant: 'error',
        })
      },
    },
  )

  const handleSubmit = (data: TypeOf<typeof schema>) => {
    mutateAsync(data)
  }

  const isDrawingRoof = useSelectionStore(
    state => state.mode === 'draw-roofs' || state.mode === 'ifc-draw-roofs',
  )
  const SubmitButton = isDrawingRoof ? HotkeySaveButton : SaveButton

  const [zComputed, setZComputed] = useState<boolean>(true)

  return (
    <>
      <Form
        key={selectedElement}
        onSubmit={data => handleSubmit(data)}
        defaultValues={defaultValues}
        validationSchema={schema}
        data-cy="form-roof-base"
      >
        <RoofFormScene zComputed={zComputed} element={selectedElement} />

        <Stack direction="column" spacing={1}>
          {roof && <EditableLabel elementGuid={roof.guid} label={roof.label ?? roof.guid} />}
          <Stack direction="row" justifyContent="end" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => setSelectedElement(null)}
              size="small"
              fullWidth
              data-cy="btn-unselect"
            >
              abwählen
            </Button>
          </Stack>
          {selectedElement && (
            <CollapsibleGeometryBoxSection defaultExpanded={true}>
              <Stack direction="column" spacing={1}>
                <FormFields
                  selectedElement={selectedElement}
                  zComputed={zComputed}
                  setZComputed={setZComputed}
                />
                <Stack direction="row" justifyContent="end" spacing={1}>
                  <SubmitButton
                    type="submit"
                    loading={isLoading}
                    fullWidth
                    disabled={false}
                    data-cy="btn-submit"
                  >
                    Speichern
                  </SubmitButton>
                  <Button
                    variant="contained"
                    onClick={() => duplicateAsync()}
                    size="small"
                    fullWidth
                    disabled={isDuplicating || !roof}
                    startIcon={<ContentCopyIcon />}
                  >
                    Duplizieren
                  </Button>
                  <HotkeyDeleteButton
                    onClick={() => deleteAsync(selectedElement)}
                    size="small"
                    fullWidth
                    loading={isDeleting}
                    disabled={false}
                  >
                    Löschen
                  </HotkeyDeleteButton>
                </Stack>
              </Stack>
            </CollapsibleGeometryBoxSection>
          )}

          <RoofSlabAlignmentForm selectedElement={selectedElement} onAligned={onClose} />
        </Stack>
      </Form>
    </>
  )
}

export default RoofFormBase
